/**
 * Centralised Tier Configuration
 *
 * Single source of truth for all tier-related visual properties.
 * Every component that renders tier badges, icons, or colors should import from here.
 *
 * Tier codes come from the DB `community_tiers.code` column (UPPERCASE).
 * Some components receive lowercase slugs (e.g. from directory API) — use
 * `getTierConfig()` which normalises both forms.
 *
 * ── Data-driven identity (white-label groundwork) ─────────────────────────
 * A tier's *identity* — its badge image (`badgeSrc`) and `accentColor` — can be
 * overridden per row from the DB (`community_tiers.icon_url` / `.accent_color`)
 * so a super_admin can re-brand a tier without a deploy. The hardcoded values
 * below are the shipped DEFAULTS and the last-resort fallback.
 *
 * The DB is seeded to exactly these defaults (Hermes migration 463), so first
 * paint is already correct and a missing/broken row degrades to the default
 * rather than a blank square. Overrides are pushed in at runtime via
 * `applyTierIdentityOverrides()` (see TierIdentityProvider) and read back by
 * the sync `getTierConfig()`; components that must reflect a live admin edit
 * without a reload subscribe via `useTierConfig()` / `useTierIdentityVersion()`.
 *
 * The fine-grained Tailwind tint tokens (avatarGradient / badgeBg / textColor /
 * ringColorClass …) stay hardcoded: they are theme-adaptive derived styling for
 * rings and tints, not the tier's identity, and are not admin-editable.
 */

import { createElement, useSyncExternalStore, type ReactNode } from 'react';

// ─── Tier Access Level ───────────────────────────────────────────────
// Maps tier codes to abstract access levels used for upgrade CTAs.

export type TierAccessLevel = 'free' | 'paid' | 'premium' | 'elite';

// ─── Tier Config Entry ───────────────────────────────────────────────

export interface TierConfigEntry {
  /** DB code (UPPERCASE) */
  code: string;
  /** Human-readable label */
  label: string;
  /** Emoji fallback — used in text-only contexts and when no badge exists. */
  emoji: string;
  /**
   * Tier badge image. A self-contained square badge (its own background) served
   * from an app path (`/icons/tiers/vault.svg`, the shipped default) or a full
   * `tier-badges` storage URL (admin re-upload). Rendered as the tier's avatar
   * square via `renderTierBadge`. Undefined for tiers without artwork (ADMIN).
   */
  badgeSrc?: string;
  /**
   * Accent hex driving the small chip/dot/ring contexts where the full badge is
   * not shown (see `renderTierChipMark`). Data-driven (DB `accent_color`).
   */
  accentColor: string;
  /** Access level bucket */
  accessLevel: TierAccessLevel;
  /** Gradient for card banners / avatar backgrounds */
  avatarGradient: string;
  /** Text color class for tier labels */
  textColor: string;
  /** Badge background + border classes */
  badgeBg: string;
  /** Gradient for accent bars (profile cards, etc.) */
  accentGradient: string;
  /** Ring color class for the avatar outer ring in the directory */
  ringColorClass: string;
}

// ─── Config Map (shipped defaults) ───────────────────────────────────

const TIER_CONFIGS: Record<string, TierConfigEntry> = {
  BASECAMP: {
    code: 'BASECAMP',
    label: 'Basecamp',
    emoji: '⛺',
    badgeSrc: '/icons/tiers/basecamp.svg',
    accentColor: '#1e88e5',
    accessLevel: 'free',
    avatarGradient: 'bg-gradient-to-br from-sky-400 to-blue-500',
    textColor: 'text-sky-700 dark:text-sky-400',
    badgeBg: 'bg-sky-400/10 border-sky-400/30',
    accentGradient: 'from-sky-400 to-blue-500',
    ringColorClass: 'ring-sky-400/40',
  },
  WUNDERPACK: {
    code: 'WUNDERPACK',
    label: 'WunderPack',
    emoji: '🔧',
    badgeSrc: '/icons/tiers/wunderpack.svg',
    accentColor: '#0e7a4e',
    accessLevel: 'paid',
    avatarGradient: 'bg-gradient-to-br from-teal-400 to-cyan-500',
    textColor: 'text-teal-700 dark:text-teal-400',
    badgeBg: 'bg-teal-400/10 border-teal-400/30',
    accentGradient: 'from-teal-400 to-cyan-500',
    ringColorClass: 'ring-teal-400/40',
  },
  VAULT: {
    code: 'VAULT',
    label: 'The Vault',
    emoji: '🏦',
    badgeSrc: '/icons/tiers/vault.svg',
    accentColor: '#d9a441',
    accessLevel: 'premium',
    avatarGradient: 'bg-gradient-to-br from-violet-400 to-purple-500',
    textColor: 'text-violet-700 dark:text-violet-400',
    badgeBg: 'bg-violet-400/10 border-violet-400/30',
    accentGradient: 'from-violet-400 to-purple-500',
    ringColorClass: 'ring-violet-400/40',
  },
  K2K: {
    code: 'K2K',
    label: 'K2K',
    emoji: '🔑',
    badgeSrc: '/icons/tiers/k2k.svg',
    accentColor: '#b57e0a',
    accessLevel: 'premium',
    avatarGradient: 'bg-gradient-to-br from-amber-400 to-orange-500',
    textColor: 'text-amber-700 dark:text-amber-400',
    badgeBg: 'bg-amber-400/10 border-amber-400/30',
    accentGradient: 'from-amber-400 to-orange-500',
    ringColorClass: 'ring-amber-400/40',
  },
  INNER_CIRCLE: {
    code: 'INNER_CIRCLE',
    label: 'Inner Circle',
    emoji: '💎',
    badgeSrc: '/icons/tiers/inner-circle.svg',
    accentColor: '#2f4d86',
    accessLevel: 'elite',
    avatarGradient: 'bg-gradient-to-br from-fuchsia-400 to-purple-500',
    textColor: 'text-fuchsia-700 dark:text-fuchsia-400',
    badgeBg: 'bg-fuchsia-400/10 border-fuchsia-400/30',
    accentGradient: 'from-fuchsia-400 to-purple-500',
    ringColorClass: 'ring-fuchsia-400/40',
  },
  FOUNDERS_TABLE: {
    code: 'FOUNDERS_TABLE',
    label: 'The Founders Table',
    emoji: '👑',
    badgeSrc: '/icons/tiers/founders-table.svg',
    accentColor: '#c9992f',
    accessLevel: 'elite',
    avatarGradient: 'bg-gradient-to-br from-slate-300 to-zinc-400',
    textColor: 'text-slate-600 dark:text-slate-400',
    badgeBg: 'bg-slate-300/10 border-slate-300/30',
    accentGradient: 'from-slate-300 to-zinc-400',
    ringColorClass: 'ring-slate-400/40',
  },
  ADMIN: {
    code: 'ADMIN',
    label: 'Admin',
    emoji: '🛡️',
    // No badge artwork — renders the emoji fallback. Filtered out of tier lists.
    accentColor: '#ef4444',
    accessLevel: 'elite',
    avatarGradient: 'bg-gradient-to-br from-red-500 to-red-700',
    textColor: 'text-red-700 dark:text-red-400',
    badgeBg: 'bg-red-400/10 border-red-400/30',
    accentGradient: 'from-red-500 to-red-700',
    ringColorClass: 'ring-red-400/40',
  },
};

// ─── Slug → Code Normalisation ───────────────────────────────────────
// The directory API returns lowercase slugs like 'inner-circle', 'vault', 'k2k'.
// This map normalises them to the UPPERCASE DB codes.

const SLUG_TO_CODE: Record<string, string> = {
  basecamp: 'BASECAMP',
  wunderpack: 'WUNDERPACK',
  'wunder-pack': 'WUNDERPACK',
  vault: 'VAULT',
  'the-vault': 'VAULT',
  k2k: 'K2K',
  keys: 'K2K',
  'keys-to-the-kingdom': 'K2K',
  'inner-circle': 'INNER_CIRCLE',
  inner_circle: 'INNER_CIRCLE',
  'founders-table': 'FOUNDERS_TABLE',
  founders_table: 'FOUNDERS_TABLE',
  'the-founders-table': 'FOUNDERS_TABLE',
  admin: 'ADMIN',
};

// ─── DB identity override store ──────────────────────────────────────
// Runtime overrides for badgeSrc / accentColor, keyed by UPPERCASE code.
// Populated once by TierIdentityProvider from community_tiers. Because the DB is
// seeded to the shipped defaults, this only ever changes what a super_admin has
// customised — so the sync fallback below is always correct on first paint.

interface TierIdentityOverride {
  badgeSrc?: string;
  accentColor?: string;
}

let identityOverrides: Record<string, TierIdentityOverride> = {};
let identityVersion = 0;
const identityListeners = new Set<() => void>();

/**
 * Replace the DB identity overrides and notify subscribers. Rows come straight
 * from `community_tiers` (icon_url / accent_color). Empty/null values are
 * ignored so a cleared column falls back to the shipped default.
 */
export function applyTierIdentityOverrides(
  rows: ReadonlyArray<{ code?: string | null; icon_url?: string | null; accent_color?: string | null }>,
): void {
  const next: Record<string, TierIdentityOverride> = {};
  for (const row of rows) {
    if (!row?.code) continue;
    const icon = row.icon_url?.trim();
    const accent = row.accent_color?.trim();
    if (!icon && !accent) continue;
    next[row.code.toUpperCase()] = {
      ...(icon ? { badgeSrc: icon } : {}),
      ...(accent ? { accentColor: accent } : {}),
    };
  }
  identityOverrides = next;
  identityVersion += 1;
  for (const l of identityListeners) l();
}

function subscribeIdentity(cb: () => void): () => void {
  identityListeners.add(cb);
  return () => identityListeners.delete(cb);
}
function getIdentityVersion(): number {
  return identityVersion;
}

/**
 * Subscribe a component to DB identity changes. Returns a version counter that
 * bumps whenever overrides are (re)applied — use it to re-read getTierConfig on
 * live admin edits. Most surfaces don't need this (defaults == seed).
 */
export function useTierIdentityVersion(): number {
  return useSyncExternalStore(subscribeIdentity, getIdentityVersion, getIdentityVersion);
}

function withIdentityOverride(base: TierConfigEntry): TierConfigEntry {
  const o = identityOverrides[base.code];
  if (!o) return base;
  return {
    ...base,
    badgeSrc: o.badgeSrc ?? base.badgeSrc,
    accentColor: o.accentColor ?? base.accentColor,
  };
}

const DEFAULT_CONFIG: TierConfigEntry = TIER_CONFIGS.BASECAMP;

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Get tier config by code (UPPERCASE) or slug (lowercase), with any DB identity
 * override applied. Always returns a valid config (defaults to Basecamp).
 */
export function getTierConfig(codeOrSlug: string | null | undefined): TierConfigEntry {
  if (!codeOrSlug) return withIdentityOverride(DEFAULT_CONFIG);
  // Try direct match (UPPERCASE DB code)
  if (TIER_CONFIGS[codeOrSlug]) return withIdentityOverride(TIER_CONFIGS[codeOrSlug]);
  // Try slug normalisation
  const normalised = SLUG_TO_CODE[codeOrSlug.toLowerCase()];
  if (normalised && TIER_CONFIGS[normalised]) return withIdentityOverride(TIER_CONFIGS[normalised]);
  return withIdentityOverride(DEFAULT_CONFIG);
}

/**
 * React hook variant of `getTierConfig` that re-renders on live DB identity
 * edits. Use in the admin editor and any surface that must reflect an override
 * without a reload; the plain sync `getTierConfig` is fine everywhere else.
 */
export function useTierConfig(codeOrSlug: string | null | undefined): TierConfigEntry {
  useTierIdentityVersion();
  return getTierConfig(codeOrSlug);
}

/**
 * Resolve a tier code/slug to its UPPERCASE DB code WITHOUT defaulting.
 * Returns null for null / empty / 'all' / unmatched — unlike getTierConfig,
 * which defaults to Basecamp. Use this for FILTERING by tier so that
 * null/unmapped content isn't misfiled into Basecamp.
 */
export function resolveTierCode(codeOrSlug: string | null | undefined): string | null {
  if (!codeOrSlug) return null;
  if (TIER_CONFIGS[codeOrSlug]) return codeOrSlug;
  const lower = codeOrSlug.toLowerCase();
  if (lower === 'all') return null;
  const normalised = SLUG_TO_CODE[lower];
  return normalised && TIER_CONFIGS[normalised] ? normalised : null;
}

/**
 * Get all tier configs (for iteration, e.g. rendering all community cards),
 * with DB identity overrides applied.
 */
export function getAllTierConfigs(): TierConfigEntry[] {
  return Object.values(TIER_CONFIGS).map(withIdentityOverride);
}

/**
 * Get the ring color class for a tier — used by avatar wrappers in the directory.
 */
export function tierRingColorClass(codeOrSlug: string | null | undefined): string {
  return getTierConfig(codeOrSlug).ringColorClass;
}

/**
 * Render a tier's full badge — the self-contained square artwork. Fills its
 * container, so wrap in a square element with `overflow-hidden` + rounding.
 * Falls back to the emoji for tiers without artwork (ADMIN).
 */
export function renderTierBadge(config: TierConfigEntry, className?: string): ReactNode {
  if (config.badgeSrc) {
    return createElement('img', {
      src: config.badgeSrc,
      alt: '',
      draggable: false,
      className: `${className ?? 'w-full h-full'} object-cover select-none`,
    });
  }
  return config.emoji;
}

/**
 * Render a tier's mark for small chip/pill contexts, sized in px. At ≥18px it
 * shows a mini rounded badge; below that a full-colour badge reads as a blob, so
 * it falls back to a solid accent dot. Keeps legibility across all chip sizes.
 */
export function renderTierChipMark(config: TierConfigEntry, sizePx = 14): ReactNode {
  if (config.badgeSrc && sizePx >= 18) {
    return createElement('img', {
      src: config.badgeSrc,
      alt: '',
      draggable: false,
      className: 'rounded-[3px] object-cover select-none flex-none',
      style: { width: sizePx, height: sizePx },
    });
  }
  const dot = Math.max(6, Math.round(sizePx * 0.6));
  return createElement('span', {
    'aria-hidden': true,
    className: 'inline-block rounded-full flex-none',
    style: { width: dot, height: dot, backgroundColor: config.accentColor },
  });
}

/**
 * Access level display config — used by upgrade modals and tier badges.
 */
export const ACCESS_LEVEL_CONFIG: Record<TierAccessLevel, {
  label: string;
  color: string;
  bg: string;
}> = {
  free: { label: 'Free', color: 'text-[hsl(var(--success))]', bg: 'bg-[hsl(var(--success-light))]' },
  paid: { label: 'Member', color: 'text-[hsl(var(--info))]', bg: 'bg-[hsl(var(--info-light))]' },
  premium: { label: 'Premium', color: 'text-[hsl(var(--chart-6))]', bg: 'bg-[hsl(var(--chart-6)/0.15)]' },
  elite: { label: 'Invite Only', color: 'text-foreground', bg: 'bg-muted' },
};

/**
 * Upgrade CTA config per access level.
 */
export const UPGRADE_CTA: Partial<Record<TierAccessLevel, {
  label: string;
  price: string;
  description: string;
}>> = {
  paid: { label: 'Upgrade to Member', price: '$49/mo', description: 'Unlock courses, asset library, and implementation support' },
  premium: { label: 'Upgrade to Premium', price: '$149/mo', description: 'Access advanced strategies, expert workshops, and exclusive tools' },
  elite: { label: 'Request Invitation', price: 'By invite', description: 'Direct founder access, retreats, and bespoke advisory' },
};
